/**
 * The case's activity feed — who did what, newest first — for the
 * Queue preview pane. Replaces the "Case chain" chips: the chain said
 * what the case IS, the feed says what has been DONE on it (triage
 * moves, messages, approvals, partner events, resolved queue items).
 */

import { useEffect, useState } from 'react';

import { Badge, Button, Group, Stack, Text } from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowBackUp,
  IconArrowRight,
  IconArrowUpRight,
  IconCheck,
  IconFileDescription,
  IconFlag,
  IconInbox,
  IconMail,
  IconMessage,
  IconNote,
  IconPlayerPlay,
  IconThumbDown,
  IconThumbUp,
  IconUsers,
  IconX,
} from '@tabler/icons-react';

import { vnextApiClient } from '@/services/vnext-api';
import type { CaseActivity, CaseActivityEvent } from '@/types/case';
import { timeAgo } from './timeAgo';

/** Rows shown before "Show all". */
export const ACTIVITY_PREVIEW_ROWS = 12;

const ICONS: Record<string, React.ComponentType<{ size?: number; stroke?: number }>> = {
  enquiry_received: IconMail,
  enquiry_resolved: IconCheck,
  request_created: IconFileDescription,
  triage_advanced: IconArrowRight,
  triage_reverted: IconArrowBackUp,
  request_rejected: IconX,
  report_started: IconPlayerPlay,
  report_completed: IconFlag,
  report_failed: IconAlertTriangle,
  report_approved: IconThumbUp,
  approval_revoked: IconThumbDown,
  feedback_submitted: IconMessage,
  message_client: IconMessage,
  message_partner: IconMessage,
  message_analyst: IconMessage,
  internal_note: IconNote,
  partner_locked: IconUsers,
  partner_dispatched: IconUsers,
  partner_submitted: IconUsers,
  partner_rework: IconUsers,
  inbox_resolved: IconInbox,
  inbox_dismissed: IconInbox,
  escalated: IconArrowUpRight,
};

const ACTOR_COLOR: Record<CaseActivityEvent['actorKind'], string> = {
  analyst: 'var(--mantine-color-navy-7, var(--mantine-color-dark-6))',
  client: 'var(--mantine-color-violet-6)',
  partner: 'var(--mantine-color-teal-7)',
  system: 'var(--mantine-color-gray-5)',
};

export function activityIcon(kind: string) {
  return ICONS[kind] ?? IconArrowRight;
}

/** "Jordan Blake" / "the client" / "a partner" / nothing for system. */
export function actorLabel(event: CaseActivityEvent): string | null {
  if (event.actorName) {
    return event.actorName;
  }
  if (event.actorUserId) {
    return event.actorUserId;
  }
  switch (event.actorKind) {
    case 'client':
      return 'Client';
    case 'partner':
      return 'Partner';
    default:
      return null;
  }
}

function EventRow({ event, familyKey }: Readonly<{ event: CaseActivityEvent; familyKey: string }>) {
  const Icon = activityIcon(event.kind);
  const actor = actorLabel(event);
  const showCodename = event.entityCodename !== null && event.entityCodename !== familyKey;
  return (
    <Group gap={8} wrap="nowrap" align="flex-start" data-testid="activity-event">
      <Text component="span" style={{ color: ACTOR_COLOR[event.actorKind], flex: 'none' }} mt={2}>
        <Icon size={14} stroke={1.8} />
      </Text>
      <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
        <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
          <Text size="xs" style={{ minWidth: 0 }} lineClamp={2}>
            {event.summary}
            {actor && (
              <Text component="span" size="xs" c="dimmed">
                {' '}
                · {actor}
              </Text>
            )}
          </Text>
          {showCodename && (
            <Badge size="xs" variant="outline" color="gray" style={{ flex: 'none' }}>
              {event.entityCodename}
            </Badge>
          )}
          <Text size="xs" c="dimmed" ml="auto" style={{ whiteSpace: 'nowrap', flex: 'none' }}>
            {timeAgo(event.occurredAt)}
          </Text>
        </Group>
        {event.detail && (
          <Text size="xs" c="dimmed" lineClamp={2} style={{ wordBreak: 'break-word' }}>
            {event.detail}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

interface ActivityFeedProps {
  familyKey: string;
}

/** Fetches the family's activity lazily for the selected item. */
export function ActivityFeed({ familyKey }: Readonly<ActivityFeedProps>) {
  const [activity, setActivity] = useState<CaseActivity | null>(null);
  const [failed, setFailed] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setActivity(null);
    setFailed(false);
    setShowAll(false);
    void (async () => {
      try {
        const result = await vnextApiClient.getCaseActivity(familyKey);
        if (!cancelled) {
          setActivity(result);
        }
      } catch {
        if (!cancelled) {
          setFailed(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [familyKey]);

  if (failed) {
    return (
      <Text size="xs" c="dimmed" data-testid="activity-unavailable">
        Activity unavailable.
      </Text>
    );
  }
  if (!activity) {
    return (
      <Text size="xs" c="dimmed">
        Loading…
      </Text>
    );
  }
  if (activity.events.length === 0) {
    return (
      <Text size="xs" c="dimmed" data-testid="activity-empty">
        Nothing recorded on this case yet.
      </Text>
    );
  }

  const visible = showAll ? activity.events : activity.events.slice(0, ACTIVITY_PREVIEW_ROWS);
  const hidden = activity.events.length - visible.length;

  return (
    <Stack gap={8} data-testid="activity-feed">
      {visible.map((event) => (
        <EventRow key={event.id} event={event} familyKey={familyKey} />
      ))}
      {hidden > 0 && (
        <Button
          variant="subtle"
          size="compact-xs"
          onClick={() => setShowAll(true)}
          data-testid="activity-show-all"
          style={{ alignSelf: 'flex-start' }}
        >
          Show {hidden} more
        </Button>
      )}
      {showAll && activity.truncated && (
        <Text size="xs" c="dimmed">
          Older activity not shown.
        </Text>
      )}
    </Stack>
  );
}
